import React, { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import L from 'leaflet';
import ReactDistortableImageOverlay from 'react-leaflet-distortable-imageoverlay';
import { ImageOverlay as ImageOverlayMap, useLeaflet } from 'react-leaflet';
import { useRouteMatch } from 'react-router-dom';
import { paths } from '../../Root/paths';

import { Dispatch, iRootState } from '../../../store';
import getBlobImage from '../../../utils/getBlobImage';
import { EditPlanMode } from '../../../models/types';
import {
  constrainRectangularCornersIfNeeded,
  cornersToGbox,
  createLeafletMapProjector,
  getAspectRatioFromCorners,
  ILatLngLike,
  readGboxCorners
} from '../../../utils/planCorners.utils';

const mapState = (state: iRootState) => ({
  map: state.infrastructureMap.map,
  control: state.infrastructureMap.control,
  office: state.infrastructureOffices.item,
  isGeo: state.infrastructureMap.layers.isGeo,
  editPlan: state.infrastructureMap.editPlan,
  planImg: state.infrastructureMap.planImg
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getMap: d.infrastructureMap.getMap,
    resetMap: d.infrastructureMap.resetMap,
    setControl: d.infrastructureMap.setControl,
    setCoordinates: d.infrastructureMap.setCoordinates,
    getPlanImg: d.infrastructureMap.getPlanImg,
    resetPlanImg: d.infrastructureMap.resetPlanImg
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type IProps = { mode: EditPlanMode } & connectedProps;

const ImageOverlay: FC<IProps> = ({
  map,
  office,
  getMap,
  isGeo,
  setControl,
  setCoordinates,
  control,
  editPlan,
  mode,
  getPlanImg,
  planImg,
  resetPlanImg
}) => {
  const { previewFormat, preview, gbox, bbox } = map;
  const { map: useMap } = useLeaflet();
  const isCreate = useRouteMatch(paths.map.office.plan.create)?.isExact;
  const sourceCorners = useMemo(() => readGboxCorners(gbox), [gbox]);
  const [editedCorners, setEditedCorners] = useState<ILatLngLike[] | null>(null);
  const [overlayKey, setOverlayKey] = useState(0);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const LatLng = L.latLng as any;
  const overlayCorners = editedCorners ?? sourceCorners;

  useEffect(() => {
    return () => {
      resetPlanImg();
    };
  }, []);

  // Зависим от id офиса, а не от объекта: обновление объекта офиса без смены id
  // (например, при переходе по уведомлению) не должно вызывать параллельный
  // refetch плана наперегонки с явным getMap
  const officeId = office ? office.id : null;

  useEffect(() => {
    if (!isCreate) {
      getMap();
    }
  }, [officeId, isCreate]);

  useEffect(() => {
    if (map.id && map.id !== 'all') {
      resetPlanImg();
      getPlanImg(map.id);
    } else if (map.id === 'all') {
      resetPlanImg();
    }
  }, [map.id, map.contentId]);

  useEffect(() => {
    setEditedCorners(null);
    setOverlayKey(0);
  }, [map.id, map.contentId]);

  useEffect(() => {
    if (isCreate) {
      return;
    }

    if (useMap && map.id && map.id !== 'all') {
      if (isGeo && sourceCorners) {
        useMap.fitBounds(
          sourceCorners.map(corner => new LatLng(corner.lat, corner.lng)),
          {
            padding: [150, 150],
            maxZoom: 20
          }
        );
      } else if (!isGeo && bbox) {
        useMap.fitBounds(
          [new LatLng(bbox[1], bbox[0]), new LatLng(bbox[3], bbox[2])],
          {
            padding: [150, 150],
            maxZoom: 20
          }
        );
      } else if (office?.location) {
        setControl({
          ...control,
          zoom: 19.5,
          center: [office.location.lat, office.location.lon] as any
        });
        return;
      }

      const centerLL = useMap.getCenter();
      if (centerLL) {
        setControl({
          ...control,
          zoom: useMap.getZoom(),
          center: [centerLL.lat, centerLL.lng] as any
        });
      }
    } else if (map.id === 'all' && office?.location) {
      setControl({
        ...control,
        zoom: 19.5,
        center: [office.location.lat, office.location.lon] as any
      });
    } else if (office?.location && !map.id) {
      setControl({
        ...control,
        zoom: 19.5,
        center: [office.location.lat, office.location.lon] as any
      });
    }
  }, [map, isGeo, isCreate]);

  const previewUrl = getBlobImage(preview, previewFormat);
  const overlayUrl = editPlan && preview ? previewUrl : planImg || previewUrl;

  const cornersKey = overlayCorners
    ? overlayCorners.map(corner => `${corner.lat},${corner.lng}`).join('|')
    : '';

  const leafletCorners = useMemo(
    () => overlayCorners?.map(corner => new LatLng(corner.lat, corner.lng)) ?? [],
    [cornersKey, overlayKey]
  );

  const overlayImageKey = `${map.contentId}-${overlayKey}-${cornersKey}`;

  useEffect(() => {
    // The distortable layer enables its edit handles on the image 'load' event, which can fire
    // before a freshly (re)mounted layer is attached to the map — enabling editing then throws
    // "Cannot read properties of null (reading 'addLayer')". Keep edit mode off until the
    // overlay's key settles (one commit after every overlay remount, including the
    // corner-constrain remount), so that first 'load' runs disabled and editing is enabled
    // afterwards via the library's update path, where the layer is already attached.
    setSettledKey(overlayImageKey);
  }, [overlayImageKey]);

  const shouldConstrainCorners =
    mode === EditPlanMode.Rotate || mode === EditPlanMode.Scale;

  const onCornersUpdated = (corners: any) => {
    const incomingCorners = corners.map((corner: { lat: number; lng: number }) => ({
      lat: corner.lat,
      lng: corner.lng
    }));

    let nextCorners = incomingCorners;
    let changed = false;

    if (shouldConstrainCorners && useMap && sourceCorners) {
      const projector = createLeafletMapProjector(useMap);
      const aspectRatio = getAspectRatioFromCorners(sourceCorners, projector);
      ({ corners: nextCorners, changed } = constrainRectangularCornersIfNeeded(
        incomingCorners,
        aspectRatio,
        projector
      ));
    }

    setCoordinates(cornersToGbox(nextCorners));

    if (changed) {
      setEditedCorners(nextCorners);
      setOverlayKey(key => key + 1);
    }
  };

  return (
    <>
      {isGeo && map.id !== 'all' && overlayCorners ? (
        <ReactDistortableImageOverlay
          key={overlayImageKey}
          url={overlayUrl}
          opacity={editPlan ? 0.4 : 1}
          onCornersUpdated={onCornersUpdated}
          editMode={editPlan && settledKey === overlayImageKey ? mode : false}
          corners={leafletCorners}
        />
      ) : !isGeo && bbox ? (
        <ImageOverlayMap
          key={map.contentId}
          url={planImg || previewUrl}
          bounds={[new LatLng(bbox[1], bbox[0]), new LatLng(bbox[3], bbox[2])]}
          zIndex={0}
        />
      ) : null}
    </>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(ImageOverlay);
